<template>
    <div class="relative -mb-12 -ml-3">
        <div
            v-if="showRefreshButtonAboveForm && displayIframe"
            class="flex justify-end"
        >
            <prime-button
                text
                class="ml-3 !p-0"
                label="Refresh Credit Card Form"
                @click="handleRefreshIframe"
            />
        </div>
        <iframe
            ref="hostedIframeWindow"
            :src="paymentSourceData.url"
            class="w-full"
            :class="displayIframe ? '' : 'invisible'"
            height="280"
            @load="iframeLoaded"
        />
        <div
            v-if="!displayIframe"
            class="absolute inset-0 top-4"
        >
            <prime-skeleton />
        </div>
        <div
            v-if="!showRefreshButtonAboveForm"
            class="flex justify-end"
        >
            <prime-button
                text
                class="ml-3 !p-0"
                label="Refresh Credit Card Form"
                @click="handleRefreshIframe"
            />
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const HostedPaymentFormPaymentSourceFragment = graphql(/* GraphQL */ `
    fragment HostedPaymentFormPaymentSourceFragment on PaymentSourceCreditCard {
        provider
        url
    }
`)
</script>

<script setup lang="ts">
import PrimeSkeleton from 'primevue/skeleton'
import PrimeButton from 'primevue/button'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'

const props = defineProps<{
    paymentSource: FragmentType<typeof HostedPaymentFormPaymentSourceFragment>
    refreshButtonAboveForm?: boolean
}>()

const emit = defineEmits<{ (e: 'token', payload: string | undefined): void }>()

const paymentSourceData = computed(() =>
    getFragmentData(HostedPaymentFormPaymentSourceFragment, props.paymentSource)
)

const showRefreshButtonAboveForm = computed(() => props.refreshButtonAboveForm)

const displayIframe = ref(false)
function iframeLoaded(event: Event) {
    // Give the iframe time to load the styles
    setTimeout(() => {
        displayIframe.value = true
    }, 200)
}
function resetIframe() {
    setTimeout(() => {
        if (displayIframe.value === false) {
            emit('token', undefined)
            hostedIframeWindow.value.src = paymentSourceData.value.url
            resetIframe()
        }
    }, 4000)
}

function handleRefreshIframe() {
    emit('token', undefined)
    displayIframe.value = false
    hostedIframeWindow.value.src = paymentSourceData.value.url
    resetIframe()
}

onMounted(() => {
    resetIframe()
})

const hostedIframeWindow = ref<HTMLIFrameElement>()

const PaymentTokenExtendedTypeCreditCard = 'CREDIT-CARD'
const PaymentTokenCardPointePrefix = 'CP'
function tokenEventListener(event: MessageEvent) {
    // filter on iframe messages
    if (event.source != hostedIframeWindow.value?.contentWindow) {
        return
    }

    //
    // each provider has its own compact payment token format
    //

    if (paymentSourceData.value.provider === 'CARD_POINTE') {
        // extract token and expiry
        const parsedMessage = JSON.parse(event.data)

        // invalid card data, so emit empty token
        if (parsedMessage.message.length == 0) {
            emit('token', undefined)
            return
        }

        // CP is payment token prefix
        const token =
            PaymentTokenCardPointePrefix +
            '|' +
            parsedMessage.message +
            '|' +
            parsedMessage.expiry +
            '|' +
            PaymentTokenExtendedTypeCreditCard
        emit('token', token)
    } else {
        throw new Error()
    }
}

onMounted(() => {
    window.addEventListener('message', tokenEventListener, false)
})

onUnmounted(() => {
    window.removeEventListener('message', tokenEventListener, false)
})
</script>

<style lang="postcss"></style>
